import type { ParticipantFormData } from './registrationFlowTypes';
import type { RegisteredParticipant } from '@/state/stores/registrationStore';

export type RegistrationFlowPrimaryOpts = {
  /** JWT sub или id профиля из ЛК — участник сессии брони. */
  sessionParticipantId: string | null | undefined;
  /** Явный выбор владельца кабинета при нескольких primary (store). */
  cabinetOwnerParticipantId: string | null | undefined;
};

/**
 * Индекс единственной «главной» строки для телефона и бейджа: без двусмысленности
 * (раньше JWT и выбранный primary могли совпасть на двух карточках сразу).
 *
 * Приоритет при нескольких карточках: владелец кабинета → JWT → выбранный primary index.
 */
export function resolveRegistrationFlowPrimaryRowIndex(
  participants: ParticipantFormData[],
  primaryFormIndex: number,
  opts: RegistrationFlowPrimaryOpts,
): number {
  if (participants.length === 0) return 0;
  if (participants.length === 1) return 0;

  const oid = opts.cabinetOwnerParticipantId?.trim();
  if (oid) {
    const idx = participants.findIndex((p) => p.id?.trim() === oid);
    if (idx >= 0) return idx;
  }

  const sid = opts.sessionParticipantId?.trim();
  if (sid) {
    const idx = participants.findIndex((p) => p.id?.trim() === sid);
    if (idx >= 0) return idx;
  }

  return Math.min(Math.max(0, primaryFormIndex), participants.length - 1);
}

/**
 * Строка считается «главным» участником брони для обязательности телефона и UI «основной профиль».
 */
export function isRegistrationFlowPrimaryParticipantRow(
  _row: ParticipantFormData,
  index: number,
  participants: ParticipantFormData[],
  primaryFormIndex: number,
  opts: RegistrationFlowPrimaryOpts,
): boolean {
  if (participants.length === 0) return false;
  if (participants.length === 1) return index === 0;

  const primaryIdx = resolveRegistrationFlowPrimaryRowIndex(
    participants,
    primaryFormIndex,
    opts,
  );
  return index === primaryIdx;
}

/**
 * Id главного primary в сторе (модалки без списка форм и без explicit primary index):
 * тот же приоритет owner → JWT; иначе первый primary в порядке стора.
 */
export function resolveBookingPrimaryRegisteredParticipantId(
  participants: RegisteredParticipant[],
  opts: RegistrationFlowPrimaryOpts,
): string | null {
  const primaries = participants.filter((p) => !p.isPartner2);
  if (primaries.length === 0) return null;
  if (primaries.length === 1) {
    const id = primaries[0]!.id.trim();
    return id || null;
  }

  const oid = opts.cabinetOwnerParticipantId?.trim();
  if (oid) {
    const m = primaries.find((p) => p.id.trim() === oid);
    if (m) return m.id.trim();
  }

  const sid = opts.sessionParticipantId?.trim();
  if (sid) {
    const m = primaries.find((p) => p.id.trim() === sid);
    if (m) return m.id.trim();
  }

  const fallback = primaries[0]!.id.trim();
  return fallback || null;
}
